// Global Types
import type {
  $Model,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  ChampionshipDocumentStatus,
} from 'common.data';
import {
  docRoute,
  docType,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';
import {
  AvField,
} from 'availity-reactstrap-validation';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  cleanFormValues,
  getFileUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  i18next,
} from 'common.language';
import {
  defaultGetData,
  defaultUpsertItem,
  getDocumentUrl,
  publicBorder,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import EmptyTable from '../../../common/EmptyTable';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
};

type $State = {
  data: Array<$Model<'ChampionshipDocument'>>;
  loading: boolean;
};

class AdminChampionshipDocumentsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'ChampionshipDocument') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyAdminChampionshipDocument();

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    return (
      <SectionHeader
        bottomBorder
        title={t('Championship documents')}
      />
    );
  }

  renderButtons(dataItem: $Model<'ChampionshipDocument'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          href={dataItem.File ? getFileUrl({
            cdnUrl: config.CDN_URL,
            file: dataItem.File,
          }) : getDocumentUrl({
            cdnUrl: config.CDN_URL,
            docRoute: docRoute.adminRegattaDocument,
            docType: docType.pdf,
            id,
            language: i18next.language as $Language,
          })}
          id={`championship-document-file-button-${id}`}
          outline
          size={'sm'}
          target={'_blank'}
        >
          <em className={'far fa-file-pdf'} />
        </Button>
        <Button
          className={'mr-1'}
          color={'success'}
          id={`config-championship-document-button-${id}`}
          onClick={() => defaultUpsertItem({
            action: async () => {
              const params = {
                ...dataItem,
                PublishDate: moment.utc().toDate(),
                Status: ChampionshipDocumentStatus.confirmed,
              };

              await Services.Api.upsertAdminChampionshipDocument(params);
            },
          })}
          outline
          size={'sm'}
        >
          {t('Confirm')}
        </Button>
        <FormDialog
          fields={(
            <AvField
              label={t('Status description')}
              name={'StatusContent'}
              value={dataItem.StatusContent}
            />
          )}
          id={`reject-championship-document-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'ChampionshipDocument'>>({
                ...dataItem,
                ...values,
                Status: ChampionshipDocumentStatus.rejected,
              });

              await Services.Api.upsertAdminChampionshipDocument(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Championship document')}
        >
          <Button
            color={'danger'}
            id={`reject-championship-document-button-${dataItem.id}`}
            outline
            size={'sm'}
          >
            {t('Reject')}
          </Button>
        </FormDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Championship')}</th>
            <th>{t('Created')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'ChampionshipDocument'>) => (
            <tr
              key={dataItem.id}
              style={publicBorder(dataItem.Public)}
            >
              <td>{dataItem.Name}</td>
              <td>
                {_.get(
                  dataItem,
                  'Championship.Name',
                  '',
                )}
              </td>
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  render() {
    const {
      id,
    } = this.props;
    const {
      loading,
    } = this.state;

    return (
      <Card id={id}>
        <Loader
          loading={loading === true}
          overlay
        >
          <>
            {this.renderHeader()}
            {this.renderTable()}
          </>
        </Loader>
      </Card>
    );
  }
}

export default AdminChampionshipDocumentsSectionView;
